import {
  Datepicker,
  Eventcalendar,
  MbscCalendarEvent,
  MbscCalendarEventData,
  MbscDatepickerChangeEvent,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventUpdateEvent
} from "@mobiscroll/react";
import {
  Divider,
  Skeleton,
  Stack,
  useColorScheme,
  useMediaQuery
} from "@mui/material";
import { cloneDeep, first, last, set } from "lodash-es";
import moment from "moment-timezone";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ActionBar } from "../components/ActionBar";
import AvailabilitySubmittedDialog from "../components/Dialogs/AvailabilitySubmittedDialog";
import RemoveEventDialog from "../components/Dialogs/RemoveEventDialog";
import SubmitAvailabilityDialog from "../components/Dialogs/SubmitAvailabilityDialog";
import { DateNavigationHeader } from "../components/Header/DateNavigationHeader";
import { MeetingAvailabilityInfo } from "../components/Meeting/MeetingAvailabilityInfo";
import { AltChatUI } from "../components/Oliver/AltChatUI";
import { RequiredSlotsValidation } from "../components/RequiredSlotsValidation";
import {
  recordAvailabilitySession,
  submitAvailability
} from "../helpers/availability/availability";
import { useLegalTerms } from "../hooks/useLegalTerms";
import { useScreenSize } from "../hooks/useScreenSize";
import { useSchedulerStore } from "../state/scheduler.store";
import { getAllDatesFromRanges } from "../utils/slots";

import "../../src/assets/mobiscroll.css";
import {
  EntityTypeEnum,
  InterviewRequestSourceEnum,
  OperationTypeEnum
} from "@rooster/types";
import { useTranslation } from "react-i18next";
import MessageDialog from "../components/MessageDialog";
import { ERROR_CODES } from "../components/Tooltip/ErrorListTooltip";
import Tour from "../components/Tour/Tour";
import { initializeSchedulerStore } from "../helpers/init";
import { recordTermsAcceptanceIfApplicable } from "../helpers/meeting/recordTermsAcceptanceIfApplicable";
import { validateAvailabilityLinkHelper } from "../helpers/meeting/validate";
import { useI18nLanguage } from "../hooks/i18nLanguage";
import { getMobiscrollLocale } from "../i18n/mobiscroll";
import {
  filterAndSortFutureAvailabilities,
  findNonIntersectingParts
} from "../utils/availability";
import { disableDateNavButton, navigateWeek } from "../utils/dateNavigation";
import { formatDurationForMeetingDescription } from "../utils/duration";

export const AvailabilityView: React.FC = () => {
  const { t, i18n } = useTranslation();

  const { selectedLanguageForMoment } = useI18nLanguage();
  const { mode: themeVariant } = useColorScheme();
  const {
    meetingLink,
    sessionData,
    actions,
    clientId,
    ui,
    clientLogo,
    link,
    showTour,
    trackingId
  } = useSchedulerStore();
  const { isSmallScreen, isMediumScreen, isLargeScreen } = useScreenSize();
  const [processingBackActionState, setProcessingBackActionState] =
    useState(false);
  const showDatePickerMaximized = useMediaQuery("(min-height: 720px)");

  const calInst = useRef<Eventcalendar>(null);

  // Candidate-session telemetry: capture mount time once, and fire a single
  // best-effort session record when the availability page is ready (mirrors the
  // autobook capture in loadAvailabilityForAutobook).
  const sessionMountTimeRef = useRef(Date.now());
  const sessionRecordedKeyRef = useRef<string | null>(null);

  const [prevSavedEventsOriginalState, setPrevSavedEventsOriginalState] =
    useState<MbscCalendarEvent[]>([]);
  const [prevSavedEventsState, setPrevSavedEventsState] = useState<
    MbscCalendarEvent[]
  >([]);
  const [newEventsState, setNewEventsState] = useState<MbscCalendarEvent[]>([]);
  const [selectedEventState, setSelectedEventState] = useState<string | null>(
    null
  );

  const [errorCodes, setErrorCodes] = useState<number[]>([]);

  const [selectedLanguages, setSelectedLanguages] = useState<
    Record<string, string>
  >({});

  const [saving, setSavingState] = useState(false);

  // Dialogs
  const [dialogSubmitOpen, setDialogSubmitOpen] = useState(false);
  const [dialogRemoveEventOpen, setDialogRemoveEventOpen] = useState(false);
  const [dialogSubmittedAvailabilityOpen, setDialogSubmittedAvailabilityOpen] =
    useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageDialogDescription, setMessageDialogDescription] = useState("");
  const [messageDialogTitle, setMessageDialogTitle] = useState("");
  const [
    messageDialogSecondaryButtonText,
    setMessageDialogSecondaryButtonText
  ] = useState("");
  const [messageDialogPrimaryButtonText, setMessageDialogPrimaryButtonText] =
    useState(
      t("ROOSTER.PUBLIC-APP.DISMISS", {
        defaultValue: "Dismiss"
      })
    );
  const [
    onMessageDialogSecondaryButtonClick,
    setOnMessageDialogSecondaryButtonClick
  ] = useState<() => void>(() => {});

  const [
    isAvailabilityParametersSatisfied,
    setIsAvailabilityParametersSatisfied
  ] = useState(false);

  const shouldBlockAvailabilitySubmission = useMemo(() => {
    return (
      (meetingLink?.scheduledData?.docDataRooster as any)
        ?.shouldBlockAvailabilitySubmission ?? false
    );
  }, [
    (meetingLink?.scheduledData?.docDataRooster as any)
      ?.shouldBlockAvailabilitySubmission
  ]);

  useEffect(() => {
    if (
      shouldBlockAvailabilitySubmission &&
      !isAvailabilityParametersSatisfied
    ) {
      setErrorCodes((prev) => [
        ...prev,
        ERROR_CODES.AVAILABILITY_PARAMETERS_NOT_MET
      ]);
    } else {
      setErrorCodes((prev) =>
        prev.filter(
          (code) => code !== ERROR_CODES.AVAILABILITY_PARAMETERS_NOT_MET
        )
      );
    }
  }, [isAvailabilityParametersSatisfied, shouldBlockAvailabilitySubmission]);

  const showMessageDialog = ({
    title,
    message,
    secondaryAction,
    secondaryText,
    dismissButtonText
  }: {
    title: string;
    message: string;
    secondaryText?: string;
    dismissButtonText?: string;
    secondaryAction?: () => void;
  }) => {
    setMessageDialogTitle(title);
    setMessageDialogDescription(message);
    setMessageDialogSecondaryButtonText(secondaryText || "");
    setOnMessageDialogSecondaryButtonClick(() => secondaryAction || (() => {}));
    setMessageDialogPrimaryButtonText(
      dismissButtonText ||
        t("ROOSTER.PUBLIC-APP.DISMISS", {
          defaultValue: "Dismiss"
        })
    );
    setMessageDialogOpen(true);
  };

  const handleDialogSubmitOpen = () => {
    setDialogSubmitOpen(true);
  };

  const handleDialogClose = () => {
    setDialogSubmitOpen(false);
  };

  const handleSubmittedAvailabilityDialog = (state: boolean) => {
    setDialogSubmitOpen(state);
    if (!state) {
      document.location.reload();
    }
  };

  const handleCloseRemoveEventDialog = () => {
    setSelectedEventState(null);
    setDialogRemoveEventOpen(false);
  };

  //

  const handleSubmit = async (comment: string) => {
    if (
      meetingLink?.id !== undefined &&
      sessionData?.selectedTimezone &&
      clientId
    ) {
      setSavingState(true);
      try {
        if (meetingLink?.id && link) {
          const isValidatedSuccessfully = await validateAvailabilityLinkHelper({
            link,
            actions,
            t
          });
          if (!isValidatedSuccessfully) {
            return;
          }

          const newAvailability =
            newEventsState?.map((e) => ({
              start: moment(e.start).valueOf(),
              end: moment(e.end).valueOf(),
              allDay: e?.allDay ?? false,
              availabilityId: meetingLink?.id ?? ""
            })) ?? [];

          // Use filtered events for modified slots within valid dates
          const modifiedAvailability =
            prevSavedEventsState?.map((e) => ({
              start: moment(e.start).valueOf(),
              end: moment(e.end).valueOf(),
              allDay: e?.allDay ?? false,
              availabilityId: meetingLink?.id ?? ""
            })) ?? [];

          // Get out-of-bound slots from original state that aren't in current valid dates
          const outOfBoundSlots =
            prevSavedEventsOriginalState
              ?.filter((event) => {
                const eventDate = moment(event.start)
                  .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
                  .format("YYYY-MM-DD");
                return !validDates?.includes(eventDate);
              })
              .map((e) => ({
                start: moment(e.start).valueOf(),
                end: moment(e.end).valueOf(),
                allDay: e?.allDay ?? false,
                availabilityId: meetingLink?.id ?? ""
              })) ?? [];

          // Combine new availability, modified slots, and out-of-bound slots
          const submittedAvailability = [
            ...newAvailability,
            ...modifiedAvailability,
            ...outOfBoundSlots
          ];

          const result = await submitAvailability({
            client: clientId,
            availabilityId: meetingLink?.id,
            clientTimeZone: sessionData?.selectedTimezone,
            candidateComments: comment,
            submittedAvailability
          });
          await new Promise((resolve) => setTimeout(resolve, 8000)); // wait for 5 secs, to allow the backend to process the availability since it is done asynchronously (not ideal)
          handleDialogClose();
          setSavingState(false);
          if (result === "OK") {
            // Sequence T&C audit after the availability submit succeeds.
            // entityId is the availability doc id — already known client-side via
            // meetingLink?.id (matches the field passed into submitAvailability above).
            // In the availability flow the meeting-link document IS the
            // availability document, so meetingLinkId and entityId are the same
            // id. (Compare to the autobook flow in useBookingForm.ts where the
            // interview doc id differs from meetingLink.id.) entityType is
            // AVAILABILITY; operationType is SUBMIT. The helper resolves
            // silently on failure — never blocks the success dialog.
            if (meetingLink?.id && clientId) {
              await recordTermsAcceptanceIfApplicable({
                clientId,
                requestType: InterviewRequestSourceEnum.AVAILABILITY,
                // SUBMIT on first availability provision, UPDATE when the
                // candidate is editing previously-saved availability.
                // prevSavedEventsOriginalState is populated from the server's
                // prior availability when the link loads; an empty array
                // means no prior submission.
                operationType:
                  prevSavedEventsOriginalState.length > 0
                    ? OperationTypeEnum.UPDATE
                    : OperationTypeEnum.SUBMIT,
                entityType: EntityTypeEnum.AVAILABILITY,
                entityId: meetingLink.id,
                meetingLinkId: meetingLink.id,
                trackingId: trackingId ?? "",
                legal: legalTerms,
                selectedLanguages
              });
            }
            setDialogSubmittedAvailabilityOpen(true);
          }
        }
      } catch (e) {
        setSavingState(false);
      }
    }
  };

  const handleReset = () => {
    loadDefaultAvailability();
    setNewEventsState([]);
  };

  const handleTimezoneChangeInternal = (tz: string) => {
    actions.setSessionData({
      ...sessionData,
      selectedTimezone: tz
    });
  };

  const handleRemoveEvent = () => {
    setNewEventsState(
      newEventsState.filter((e) => e.start !== selectedEventState)
    );

    const filteredPrevEvents = prevSavedEventsState.filter(
      (e) => e.start !== selectedEventState
    );
    setPrevSavedEventsState(filteredPrevEvents);

    handleCloseRemoveEventDialog();
  };

  const handleDatepickerChange = (event: MbscDatepickerChangeEvent) => {
    if (event.value?.valueOf()) {
      calInst.current?.navigate(
        moment(event?.value.valueOf()).format("YYYY-MM-DD"),
        true
      );
      actions.setSessionData({
        ...sessionData,
        prevSelectedSlot: sessionData?.prevSelectedSlot,
        selectedSlot: {
          date: moment(event?.value.valueOf()).valueOf(),
          hour: null,
          minute: null
        }
      });
    }
  };

  const checkInsufficientDuration = (
    start: MbscEventCreateEvent["event"]["start"],
    end: MbscEventCreateEvent["event"]["end"]
  ) => {
    return (
      moment(end).diff(start, "minutes") <
      (meetingLink?.availability?.duration ?? 30)
    );
  };

  const checkOverlapInInvalidRange = (
    start: MbscEventCreateEvent["event"]["start"],
    end: MbscEventCreateEvent["event"]["end"]
  ) => {
    return invalidRanges.some(
      (range) =>
        moment(start).isBetween(range.start, range.end) ||
        moment(end).isBetween(range.start, range.end) ||
        moment(start).isSame(range.start)
    );
  };

  const findOverlappingEvents = (
    start: MbscEventCreateEvent["event"]["start"],
    end: MbscEventCreateEvent["event"]["end"]
  ) => {
    const adjStart = moment(start)
      .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
      .format();
    const adjEnd = moment(end)
      .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
      .format();

    return [...prevSavedEventsState, ...newEventsState].filter(
      (event) =>
        moment(adjStart).isBetween(event.start, event.end) ||
        moment(adjEnd).isBetween(event.start, event.end) ||
        (moment(event.start).isSameOrAfter(adjStart) &&
          moment(event.end).isSameOrBefore(adjEnd))
    );
  };

  const findDuplicateEvents = (
    start: MbscEventCreateEvent["event"]["start"],
    end: MbscEventCreateEvent["event"]["end"]
  ) => {
    const adjStart = moment(start)
      .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
      .format();
    const adjEnd = moment(end)
      .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
      .format();

    return [...prevSavedEventsState, ...newEventsState].filter(
      (event) =>
        moment(event.start).isSame(adjStart, "minute") &&
        moment(event.end).isSame(adjEnd, "minute")
    );
  };

  const handleAllDayEventOverlap = (
    newEvent: MbscCalendarEvent,
    overlappingEvents: MbscCalendarEvent[]
  ) => {
    showMessageDialog({
      title: t("ROOSTER.PUBLIC-APP.CANNOT_CREATE_EVENT", {
        defaultValue: "Cannot create event"
      }),
      message: t("ROOSTER.PUBLIC-APP.ALL_DAY_EVENT_OVERLAP", {
        defaultValue:
          "Selecting an all day availability slot will remove other existing slots on the same day. Do you want to remove them?"
      }),
      secondaryText: t("ROOSTER.PUBLIC-APP.CREATE_ALL_DAY_EVENT", {
        defaultValue: "Create All Day Event"
      }),
      secondaryAction: () => {
        const overlappingStarts = overlappingEvents.map((i) => i.start);
        const filteredEvents = (events: MbscCalendarEvent[]) =>
          events.filter((e) => !overlappingStarts.includes(e.start));

        setNewEventsState([
          ...filteredEvents(newEventsState),
          {
            title: t("ROOSTER.PUBLIC-APP.AVAILABLE", {
              defaultValue: "Available"
            }),
            start: moment(newEvent.start).format(),
            end: moment(newEvent.end).format(),
            allDay: newEvent.allDay
          }
        ]);
        setPrevSavedEventsState(filteredEvents(prevSavedEventsState));
        setMessageDialogOpen(false);
      }
    });
  };

  // Type guard function
  function isUpdateEvent(
    e: MbscEventCreateEvent | MbscEventUpdateEvent
  ): e is MbscEventUpdateEvent {
    return "oldEvent" in e;
  }

  const checkEventInValidDates = (
    start: MbscEventCreateEvent["event"]["start"],
    _end: MbscEventCreateEvent["event"]["end"]
  ) => {
    const eventDate = moment(start)
      .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
      .format("YYYY-MM-DD");
    return validDates?.includes(eventDate) ?? false;
  };

  const handleEventMutation = async (
    e: MbscEventCreateEvent | MbscEventUpdateEvent
  ) => {
    const isUpdate = e.type === "onEventUpdate";
    const isNewEvent = newEventsState.some((event) => event.id === e.event.id);

    // Check if event is within valid dates
    const isEventInValidDates = checkEventInValidDates(
      e.event.start,
      e.event.end
    );

    if (!isEventInValidDates) {
      showMessageDialog({
        title: t("ROOSTER.PUBLIC-APP.INVALID_DATE_SELECTED", {
          defaultValue: "Invalid Date Selected"
        }),
        message: t("ROOSTER.PUBLIC-APP.SELECT_FROM_AVAILABLE_DATES", {
          defaultValue: "Please select from the available dates only."
        }),
        dismissButtonText: t("ROOSTER.COMMON.OK", {
          defaultValue: "OK"
        })
      });
      setNewEventsState([...newEventsState]);
      return;
    }

    const insufficientDuration = checkInsufficientDuration(
      e.event.start,
      e.event.end
    );
    const overlapInInvalidRange = checkOverlapInInvalidRange(
      e.event.start,
      e.event.end
    );
    const overlappingEvents = findOverlappingEvents(e.event.start, e.event.end);
    const overlapInExistingEventsForAllDayEvent =
      overlappingEvents.length > 0 && e.event.allDay;
    const overlapInExistingEventsForNonAllDayEvent = overlappingEvents.some(
      (le) => le.allDay && le.id !== e.event.id
    );

    const isDuplicateEvent =
      findDuplicateEvents(e.event.start, e.event.end).length > 0;

    const startIsInPast =
      moment(e.event.start)
        .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
        .valueOf() <
      moment()
        .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
        .valueOf();

    if (startIsInPast) {
      showMessageDialog({
        title: t("ROOSTER.PUBLIC-APP.SELECT_FROM_AVAILABLE_DATES", {
          defaultValue: "Select from Available Dates"
        }),
        message: t(
          "ROOSTER.PUBLIC-APP.PROVIDE_BLOCKS_OF_TIME_ON_AVAILABLE_DAYS",
          {
            defaultValue:
              "Please provide blocks of time on days that are available."
          }
        ),
        dismissButtonText: t("ROOSTER.COMMON.OK", {
          defaultValue: "OK"
        })
      });
      setNewEventsState([...newEventsState]);
      return;
    }

    if (isDuplicateEvent) {
      showMessageDialog({
        title: t("ROOSTER.PUBLIC-APP.DUPLICATE_EVENT", {
          defaultValue: "Duplicate Event"
        }),
        message: t("ROOSTER.PUBLIC-APP.SELECT_DIFFERENT_BLOCK", {
          defaultValue:
            "The selected block overlaps with an existing availability block. Please select a different block."
        })
      });
      setNewEventsState([...newEventsState]);
      return;
    }

    if (overlapInExistingEventsForNonAllDayEvent) {
      showMessageDialog({
        title: t("ROOSTER.PUBLIC-APP.ALL_DAY_SLOT_EXIST-TITLE", {
          defaultValue: "All Day Slot Already Present"
        }),
        message: t("ROOSTER.PUBLIC-APP.ALL_DAY_SLOT_EXISTS", {
          defaultValue:
            "Availability slot cannot be created as an all day slot is already present for that day."
        })
      });
      setNewEventsState([...newEventsState]);
      return;
    }

    if (insufficientDuration) {
      showMessageDialog({
        title: t("ROOSTER.PUBLIC-APP.NEED_LARGER_BLOCK", {
          defaultValue: "Need Larger Time Block"
        }),
        message: t("ROOSTER.PUBLIC-APP.PROVIDE_BLOCKS_OF_TIME", {
          defaultValue:
            "Please provide blocks of time that are at least as the length of the interview, which is {{duration}}",
          duration: formatDurationForMeetingDescription(
            meetingLink?.availability?.duration ?? 0,
            t,
            selectedLanguageForMoment
          )
        })
      });

      if (isUpdate) {
        if (isUpdateEvent(e)) {
          if (isNewEvent) {
            setNewEventsState([
              ...newEventsState.filter((event) => event.id !== e.event.id),
              {
                title: t("ROOSTER.PUBLIC-APP.AVAILABLE", {
                  defaultValue: "Available"
                }),
                start: e.oldEvent?.start,
                end: e.oldEvent?.end,
                allDay: e.event.isAllDay ?? false
              }
            ]);
          }

          if (!isNewEvent) {
            setPrevSavedEventsState([
              ...prevSavedEventsState.filter(
                (event) => event.id !== e.event.id
              ),
              {
                title: t("ROOSTER.PUBLIC-APP.AVAILABLE", {
                  defaultValue: "Available"
                }),
                start: e.oldEvent?.start,
                end: e.oldEvent?.end,
                allDay: e.event.isAllDay ?? false
              }
            ]);
          }
        }
      }

      return;
    }

    if (overlapInInvalidRange) {
      showMessageDialog({
        title: t("ROOSTER.PUBLIC-APP.INVALID_BLOCK_SELECTED", {
          defaultValue: "Invalid Block Selected"
        }),
        message: t("ROOSTER.PUBLIC-APP.INVALID_BLOCK_OVERLAP", {
          defaultValue:
            "The selected block overlaps with an invalid range. Please select a different block."
        })
      });
      setNewEventsState([...newEventsState]);
      return;
    }

    if (overlapInExistingEventsForAllDayEvent) {
      handleAllDayEventOverlap(e.event, overlappingEvents);
      return;
    }

    const convertToAllDayEvent =
      moment(e.event.start).hour() === 0 &&
      moment(e.event.start).minute() === 0 &&
      moment(e.event.end).hour() === 0 &&
      moment(e.event.end).minute() === 0;

    if (convertToAllDayEvent && !e.event.allDay) {
      showMessageDialog({
        title: t("ROOSTER.PUBLIC-APP.ALL_DAY_EVENT", {
          defaultValue: "All Day Event"
        }),
        message: t("ROOSTER.PUBLIC-APP.CONVERTED_TO_ALL_DAY_EVENT", {
          defaultValue:
            "We have converted this event to an all day event since it starts and ends at midnight."
        }),
        dismissButtonText: t("ROOSTER.COMMON.OK", {
          defaultValue: "OK"
        })
      });
    }

    if (isUpdate) {
      setPrevSavedEventsState(
        prevSavedEventsState.map((event) =>
          e.event?.id === event?.id
            ? {
                title: t("ROOSTER.PUBLIC-APP.AVAILABLE", {
                  defaultValue: "Available"
                }),
                start: moment(e.event.start)
                  .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
                  .format(),
                end: moment(e.event.end)
                  .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
                  .format(),
                allDay: e.event.isAllDay ?? false
              }
            : event
        )
      );

      setNewEventsState(
        newEventsState.map((event) =>
          event?.id === e.event?.id
            ? {
                title: t("ROOSTER.PUBLIC-APP.AVAILABLE", {
                  defaultValue: "Available"
                }),
                start: moment(e.event.start)
                  .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
                  .format(),
                end: moment(e.event.end)
                  .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
                  .format(),
                allDay: e.event.isAllDay
              }
            : event
        )
      );

      return;
    }

    setNewEventsState([
      ...newEventsState,
      {
        title: t("ROOSTER.PUBLIC-APP.AVAILABLE", {
          defaultValue: "Available"
        }),
        start: moment(e.event.start)
          .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
          .format(),
        end: moment(e.event.end)
          .tz(sessionData?.selectedTimezone ?? moment.tz.guess(), true)
          .format(),
        allDay: e.event.isAllDay || convertToAllDayEvent,
        resize: !(e.event.isAllDay || convertToAllDayEvent)
      }
    ]);
  };

  const handleEventClick = (e: MbscEventClickEvent) => {
    if (e.event.start) {
      setSelectedEventState(e.event.start.toString());
      setDialogRemoveEventOpen(true);
    }
  };

  const handleBackToAvailability = async () => {
    setProcessingBackActionState(true);
    await initializeSchedulerStore(actions, t);
    actions.setUiState({
      currentStep: "availability",
      currentView: "availability-slot"
    });
    setProcessingBackActionState(false);
  };

  const loadDefaultAvailability = () => {
    if (sessionData?.selectedTimezone) {
      const availabilityTz =
        meetingLink?.scheduledData?.availabilityDocData?.clientTimeZone;
      if (availabilityTz) {
        const events =
          meetingLink?.scheduledData?.availabilityDocData?.availability?.map(
            (a) => {
              const start = moment(a.start)
                .tz(
                  meetingLink?.scheduledData?.availabilityDocData
                    ?.clientTimeZone ?? moment.tz.guess()
                )
                .tz(sessionData?.selectedTimezone ?? moment.tz.guess());
              const end = moment(a.end)
                .tz(
                  meetingLink?.scheduledData?.availabilityDocData
                    ?.clientTimeZone ?? moment.tz.guess()
                )
                .tz(sessionData?.selectedTimezone ?? moment.tz.guess());
              const isAllDay =
                start.hour() === 0 &&
                start.minute() === 0 &&
                end.hour() === 0 &&
                end.minute() === 0 &&
                !start.isSame(end, "day");
              const shouldShowAsAllDay =
                isAllDay && start.hour() === 0 && start.minute() === 0;

              return {
                title: t("ROOSTER.PUBLIC-APP.AVAILABLE", {
                  defaultValue: "Available"
                }),
                start: start.format(),
                end: end.format(),
                allDay: shouldShowAsAllDay,
                resize: !shouldShowAsAllDay
              };
            }
          );

        // Store the complete unfiltered events for submission
        const eventsClone = cloneDeep(events ?? []);
        setPrevSavedEventsOriginalState(eventsClone ?? []);

        // Filter events to only include those within valid date ranges for display
        const filteredEvents = events?.filter((event) => {
          const eventDate = moment(event.start)
            .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
            .format("YYYY-MM-DD");
          return validDates?.includes(eventDate) ?? false;
        });

        setPrevSavedEventsState(filteredEvents ?? []);
      }
    }
  };

  const workDayView = useMemo(() => {
    return ui?.workDayView;
  }, [ui?.workDayView]);

  const validDates = useMemo(() => {
    const ranges =
      meetingLink?.scheduledData?.docDataRooster?.selectionMode ===
      "individual_dates"
        ? meetingLink?.availability?.range?.flatMap((i) => [
            moment(i.rangeStart)
              .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
              .format("YYYY-MM-DD"),
            moment(i.rangeEnd)
              .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
              .subtract(1, "millisecond")
              .format("YYYY-MM-DD")
          ])
        : getAllDatesFromRanges(
            meetingLink?.availability?.range ?? [],
            sessionData?.selectedTimezone ?? moment.tz.guess()
          );
    // Filter out weekends
    const filteredRanges = ranges?.filter((date) => {
      const day = moment(date).day();
      return day !== 0 && day !== 6; // 0 is Sunday, 6 is Saturday
    });

    return filteredRanges?.filter((date) =>
      moment(date).endOf("day").isAfter(moment())
    );
  }, [
    meetingLink?.availability?.range,
    sessionData?.selectedTimezone,
    meetingLink?.scheduledData?.docDataRooster?.timeZone
  ]);

  const minDate = useMemo(() => {
    return moment()
      .startOf("day")
      .tz(
        meetingLink?.scheduledData?.docDataRooster?.timeZone ??
          moment.tz.guess()
      )
      .toDate();
  }, [meetingLink?.scheduledData?.docDataRooster?.timeZone]);

  const maxDate = useMemo(() => {
    return moment
      .tz(
        last(meetingLink?.availability?.range)?.rangeEnd,
        meetingLink?.scheduledData?.docDataRooster?.timeZone ??
          moment.tz.guess()
      )
      .add(1, "day")
      .toDate();
  }, [
    meetingLink?.availability?.range,
    meetingLink?.scheduledData?.docDataRooster?.timeZone
  ]);

  const invalidRanges = useMemo(() => {
    const result = findNonIntersectingParts(
      moment(minDate).startOf("week").subtract(1, "day").valueOf(),
      moment().add(60, "days").startOf("day").valueOf(),
      meetingLink?.availability?.range ?? [],
      sessionData?.selectedTimezone ?? moment.tz.guess()
    );
    return result;
  }, [
    meetingLink?.availability?.range,
    meetingLink?.scheduledData?.docDataRooster?.timeZone,
    sessionData?.selectedTimezone
  ]);

  useLayoutEffect(() => {
    document.querySelectorAll(".mbsc-calendar-row").forEach((parent) => {
      const children = Array.from(parent.children);
      if (
        children.length > 0 &&
        children.every((child) => child.classList.contains("mbsc-disabled"))
      ) {
        (parent as HTMLElement).style.display = showDatePickerMaximized
          ? ""
          : "none"; // All children have the class
      }
    });
  }, [showDatePickerMaximized]);

  useEffect(() => {
    if (sessionData?.selectedSlot?.date) {
      calInst.current?.navigate(moment(sessionData?.selectedSlot?.date));
    }
  }, [sessionData?.selectedSlot?.date]);

  // Fire-and-forget candidate-session record once the availability page is
  // ready (link loaded + timezone resolved). Guarded by request-session start
  // so ordinary rerenders do not duplicate rows, while edit/reschedule actions
  // on the same loaded page can create their own history row.
  useEffect(() => {
    if (!meetingLink?.id || !clientId || !sessionData?.selectedTimezone) {
      return;
    }
    const requestSessionStartedAt =
      sessionData.requestSessionStartedAt ?? sessionMountTimeRef.current;
    const actionType = sessionData?.isRescheduleFlow
      ? "reschedule"
      : "schedule";
    const sessionRecordedKey = `${meetingLink.id}:${actionType}:${requestSessionStartedAt}`;
    if (sessionRecordedKeyRef.current === sessionRecordedKey) {
      return;
    }
    sessionRecordedKeyRef.current = sessionRecordedKey;
    recordAvailabilitySession({
      clientId,
      meetingLink,
      sessionData,
      mountTime: sessionMountTimeRef.current
    });
  }, [
    meetingLink?.id,
    clientId,
    sessionData?.selectedTimezone,
    sessionData?.requestSessionStartedAt,
    sessionData?.isRescheduleFlow
  ]);

  useEffect(() => {
    loadDefaultAvailability();
    const newEvents = newEventsState.map((event) => {
      const startMoment = moment(event.start).tz(
        sessionData?.selectedTimezone ?? moment.tz.guess()
      );
      const endMoment = moment(event.end).tz(
        sessionData?.selectedTimezone ?? moment.tz.guess()
      );

      // An event is all-day if it starts at beginning of day and ends at beginning of next day
      const isAllDay =
        startMoment.hour() === 0 &&
        startMoment.minute() === 0 &&
        endMoment.hour() === 0 &&
        endMoment.minute() === 0;

      return {
        ...event,
        allDay: isAllDay,
        start: startMoment.format(),
        end: endMoment.format()
      };
    });
    setNewEventsState(newEvents);
  }, [sessionData?.selectedTimezone]);

  useEffect(() => {
    actions.setSessionData({
      ...sessionData,
      selectedSlot: {
        date: moment(validDates?.[0]).valueOf() ?? null,
        hour: null,
        minute: null
      }
    });
  }, [validDates]);

  // Filter events to only show those within valid dates
  const filteredPrevSavedEventsState = useMemo(() => {
    return prevSavedEventsState.filter((event) => {
      const eventDate = moment(event.start)
        .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
        .format("YYYY-MM-DD");
      return validDates?.includes(eventDate) ?? false;
    });
  }, [prevSavedEventsState, validDates, sessionData?.selectedTimezone]);

  const filteredNewEventsState = useMemo(() => {
    return newEventsState.filter((event) => {
      const eventDate = moment(event.start)
        .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
        .format("YYYY-MM-DD");
      return validDates?.includes(eventDate) ?? false;
    });
  }, [newEventsState, validDates, sessionData?.selectedTimezone]);

  const hasAnyEventsChanged = useMemo(() => {
    const newEventsExists = filteredNewEventsState.length > 0;
    const prevEventsModified =
      filteredPrevSavedEventsState.length !==
        prevSavedEventsOriginalState.length ||
      filteredPrevSavedEventsState.some(
        (currentEvent) =>
          !prevSavedEventsOriginalState.some(
            (originalEvent) =>
              originalEvent.start === currentEvent.start &&
              originalEvent.end === currentEvent.end
          )
      );

    // Show Reset button if there are any changes
    return newEventsExists || prevEventsModified;
  }, [
    filteredNewEventsState,
    filteredPrevSavedEventsState,
    prevSavedEventsOriginalState
  ]);

  const saveButtonEnabled = useMemo(() => {
    // Check if availability submission should be blocked based on slots fulfillment
    if (shouldBlockAvailabilitySubmission) {
      return isAvailabilityParametersSatisfied;
    }

    return hasAnyEventsChanged;
  }, [
    hasAnyEventsChanged,
    isAvailabilityParametersSatisfied,
    shouldBlockAvailabilitySubmission
  ]);

  const handlePreviousWeek = () =>
    navigateWeek("previous", sessionData, actions, validDates);
  const handleNextWeek = () =>
    navigateWeek("next", sessionData, actions, validDates);

  const disableDateNavPrevButton = useMemo(
    () => disableDateNavButton("previous", sessionData, validDates),
    [sessionData?.selectedSlot, sessionData?.computedAvailability]
  );

  const disableDateNavNextButton = useMemo(
    () => disableDateNavButton("next", sessionData, validDates),
    [sessionData?.selectedSlot, sessionData?.computedAvailability]
  );

  const submittedAvailabilities = useMemo(() => {
    return meetingLink?.scheduledData?.availabilityDocData?.availability ?? [];
  }, [meetingLink?.scheduledData?.availabilityDocData]);

  const availabilityTimezone = useMemo(() => {
    return (
      meetingLink?.scheduledData?.availabilityDocData?.clientTimeZone ??
      moment.tz.guess()
    );
  }, [meetingLink?.scheduledData?.availabilityDocData]);

  const futureSubmittedAvailabilities = useMemo(() => {
    return filterAndSortFutureAvailabilities(
      submittedAvailabilities,
      availabilityTimezone
    );
  }, [submittedAvailabilities, availabilityTimezone]);

  const customScheduleEventContentRenderer = useCallback(
    (_: MbscCalendarEventData) => {
      return t("ROOSTER.PUBLIC-APP.AVAILABLE", {
        defaultValue: "Available"
      });
    },
    []
  );

  // Show tour for first-time users
  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenSchedulerTour");
    if (!hasSeenTour && sessionData && !sessionData.expiredLink) {
      actions.setShowTour(true);
    }
  }, [sessionData]);

  const handleCloseTour = () => {
    actions.setShowTour(false);
    localStorage.setItem("hasSeenSchedulerTour", "true");
  };

  const legalTerms = useLegalTerms();

  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      width="100%"
      maxHeight="100dvh"
      sx={{
        scrollbarColor:
          themeVariant === "dark" ? "gray rgba(18, 18, 18, 0.3)" : ""
      }}
    >
      <Tour isOpen={Boolean(showTour)} onClose={handleCloseTour} />
      {/* Dialogs */}
      <SubmitAvailabilityDialog
        open={dialogSubmitOpen}
        handleClose={handleDialogClose}
        handleSave={handleSubmit}
        saving={saving}
        legal={legalTerms}
        key={`terms-${legalTerms?.length ?? "0"}-${meetingLink?.id || "default"}`}
        selectedLanguages={selectedLanguages}
        onSelectedLanguagesChange={setSelectedLanguages}
      />
      <RemoveEventDialog
        open={dialogRemoveEventOpen}
        handleClose={handleCloseRemoveEventDialog}
        handleRemove={handleRemoveEvent}
      />
      <AvailabilitySubmittedDialog
        open={dialogSubmittedAvailabilityOpen}
        handleClose={() => handleSubmittedAvailabilityDialog(false)}
      />
      <MessageDialog
        open={messageDialogOpen}
        title={messageDialogTitle}
        dismissButtonText={messageDialogPrimaryButtonText}
        message={messageDialogDescription}
        onClose={() => setMessageDialogOpen(false)}
        secondaryButtonText={messageDialogSecondaryButtonText}
        onSecondaryButtonClick={onMessageDialogSecondaryButtonClick}
      />

      {/* Main Content */}
      <Stack
        direction={isSmallScreen || isMediumScreen ? "column" : "row"}
        divider={
          <Divider
            orientation={
              isSmallScreen || isMediumScreen ? "horizontal" : "vertical"
            }
            flexItem
          />
        }
        minWidth="100%"
        maxHeight="100dvh"
      >
        {/* Info Col */}
        <Stack
          pl={isSmallScreen ? "3vw" : 3}
          pr="1vw"
          pt={1}
          justifyContent="space-between"
          maxHeight="100dvh"
        >
          <Stack>
            <MeetingAvailabilityInfo
              clientLogo={clientLogo}
              uiStep={ui?.currentStep ?? "loading"}
              meetingDuration={meetingLink?.availability?.duration ?? null}
              handleTimezoneChange={handleTimezoneChangeInternal}
              selectedTimezone={
                sessionData?.selectedTimezone ??
                meetingLink?.scheduledData?.availabilityDocData
                  ?.clientTimeZone ??
                moment.tz.guess()
              }
              hidesubmittedAvailabilities={true}
              submittedAvailabilities={
                meetingLink?.scheduledData?.availabilityDocData?.availability ??
                []
              }
              submittedAvailabilitiesTz={
                meetingLink?.scheduledData?.availabilityDocData
                  ?.clientTimeZone ?? ""
              }
              renderControls={
                isSmallScreen ? (
                  <ActionBar
                    errors={errorCodes}
                    onReset={handleReset}
                    onBack={
                      prevSavedEventsState?.length > 0
                        ? handleBackToAvailability
                        : undefined
                    }
                    onSubmit={handleDialogSubmitOpen}
                    showBackButton={futureSubmittedAvailabilities?.length > 0}
                    isSubmitDisabled={saveButtonEnabled}
                    isResetDisabled={!hasAnyEventsChanged}
                    isHandlingBack={processingBackActionState}
                    showPoweredBy={false}
                    middleContent={
                      <RequiredSlotsValidation
                        setIsAvailabilityParametersSatisfied={
                          setIsAvailabilityParametersSatisfied
                        }
                        totalSlots={
                          (meetingLink?.scheduledData?.docDataRooster as any)
                            ?.totalInterviewSlots ?? 0
                        }
                        availableDaysRequired={
                          (meetingLink?.scheduledData?.docDataRooster as any)
                            ?.availableDaysRequired ?? 0
                        }
                        minSlotsAcrossDays={
                          (meetingLink?.scheduledData?.docDataRooster as any)
                            ?.minSlotsAcrossDays ?? 0
                        }
                        duration={meetingLink?.availability?.duration ?? 30}
                        currentSlots={[
                          ...filteredPrevSavedEventsState,
                          ...filteredNewEventsState
                        ]}
                        timezone={
                          sessionData?.selectedTimezone ?? moment.tz.guess()
                        }
                        validDates={validDates}
                        inline
                      />
                    }
                  />
                ) : null
              }
            />
          </Stack>
          {isLargeScreen && (
            <Stack px={-3} pb={3}>
              {clientId ? (
                <Datepicker
                  selectMultiple={false}
                  onChange={handleDatepickerChange}
                  min={moment(first(validDates))
                    .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
                    .format()}
                  max={moment(last(meetingLink?.availability?.range)?.rangeEnd)
                    .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
                    .format()}
                  display="inline"
                  calendarType="month"
                  valid={validDates}
                  value={
                    sessionData?.selectedSlot?.date ??
                    sessionData?.computedAvailability?.[0]?.date ??
                    moment().valueOf()
                  }
                  themeVariant={themeVariant === "light" ? "light" : "dark"}
                  locale={getMobiscrollLocale(t, i18n.language)}
                />
              ) : (
                <Skeleton variant="rounded" width="100%" height="300px" />
              )}
            </Stack>
          )}
        </Stack>

        {/* Scheduler */}
        {/* WCAG 2.1 A: scrollable-region-focusable — add tabIndex/role when overflow:auto is active */}
        <Stack
          flex={1}
          minWidth="60%"
          divider={<Divider orientation="horizontal" flexItem />}
          height={isSmallScreen || isMediumScreen ? "100%" : "100dvh"}
          overflow={isSmallScreen || isMediumScreen ? "auto" : ""}
          tabIndex={isSmallScreen || isMediumScreen ? 0 : undefined}
          role={isSmallScreen || isMediumScreen ? "region" : undefined}
          aria-label={
            isSmallScreen || isMediumScreen
              ? t("ROOSTER.AVAILABILITY.CALENDAR-SCHEDULER-REGION", {
                  defaultValue: "Calendar scheduler"
                })
              : undefined
          }
          sx={{ position: "relative" }}
        >
          <Stack direction="row">
            {!isSmallScreen && (
              <DateNavigationHeader
                selectedDate={sessionData?.selectedSlot?.date}
                isLoading={!clientId}
                disablePrev={disableDateNavPrevButton}
                disableNext={disableDateNavNextButton}
                onPreviousWeek={handlePreviousWeek}
                onNextWeek={handleNextWeek}
                showNavigation={true}
              />
            )}
            {isMediumScreen && (
              <ActionBar
                errors={errorCodes}
                onReset={handleReset}
                showHelp={() => {
                  actions.setShowTour(true);
                }}
                onBack={
                  prevSavedEventsState?.length > 0
                    ? handleBackToAvailability
                    : undefined
                }
                onSubmit={handleDialogSubmitOpen}
                showBackButton={futureSubmittedAvailabilities?.length > 0}
                isSubmitDisabled={saveButtonEnabled}
                isResetDisabled={!hasAnyEventsChanged}
                isHandlingBack={processingBackActionState}
                showPoweredBy={false}
                middleContent={
                  <RequiredSlotsValidation
                    setIsAvailabilityParametersSatisfied={
                      setIsAvailabilityParametersSatisfied
                    }
                    totalSlots={
                      (meetingLink?.scheduledData?.docDataRooster as any)
                        ?.totalInterviewSlots ?? 0
                    }
                    availableDaysRequired={
                      (meetingLink?.scheduledData?.docDataRooster as any)
                        ?.availableDaysRequired ?? 0
                    }
                    minSlotsAcrossDays={
                      (meetingLink?.scheduledData?.docDataRooster as any)
                        ?.minSlotsAcrossDays ?? 0
                    }
                    duration={meetingLink?.availability?.duration ?? 30}
                    currentSlots={[
                      ...filteredPrevSavedEventsState,
                      ...filteredNewEventsState
                    ]}
                    timezone={
                      sessionData?.selectedTimezone ?? moment.tz.guess()
                    }
                    validDates={validDates}
                    inline
                  />
                }
              />
            )}
          </Stack>
          {/* WCAG 2.1 A: scrollable-region-focusable — this wrapper is only the
              scroll container on desktop (overflow:auto), where it must be
              keyboard-focusable and labelled. On mobile (RSTR-24222) the
              calendar owns its own bounded scroll, so this wrapper must NOT
              scroll — and therefore must NOT be a focusable landmark either
              (an extra tab stop / a "region" that isn't the scroller). Gate the
              a11y props on the same breakpoint as `overflow`. */}
          <Stack
            display="inline"
            direction="row"
            spacing={2}
            overflow={isSmallScreen || isMediumScreen ? "visible" : "auto"}
            tabIndex={isSmallScreen || isMediumScreen ? undefined : 0}
            role={isSmallScreen || isMediumScreen ? undefined : "region"}
            aria-label={
              isSmallScreen || isMediumScreen
                ? undefined
                : t("ROOSTER.AVAILABILITY.INTERVIEW-CALENDAR-REGION", {
                    defaultValue: "Interview calendar"
                  })
            }
          >
            <Eventcalendar
              locale={getMobiscrollLocale(t, i18n.language)}
              data={[
                ...filteredPrevSavedEventsState,
                ...filteredNewEventsState
              ]}
              onEventCreate={handleEventMutation}
              onEventClick={handleEventClick}
              onEventUpdate={handleEventMutation}
              dragToMove
              ref={calInst}
              clickToCreate={true}
              dragInTime
              dragToCreate
              dragToResize
              dragTimeStep={15}
              themeVariant={themeVariant === "light" ? "light" : "dark"}
              showControls={isSmallScreen}
              min={minDate}
              max={maxDate}
              // RSTR-24222: on mobile the schedule renders a full-day (24h)
              // grid taller than the viewport. Without a bounded height,
              // Mobiscroll's own scroll container (.mbsc-schedule-grid-scroll)
              // expands to full content height and never scrolls, so an outer
              // MUI wrapper becomes the scroller instead — and Mobiscroll's
              // native drag-to-edge auto-scroll (which also extends the
              // selection) only acts on its own scroller, leaving the drag
              // trapped within the visible hours. Bounding the calendar height
              // hands the scroll back to Mobiscroll so drag-auto-scroll works.
              height={isSmallScreen || isMediumScreen ? "100dvh" : undefined}
              view={{
                schedule: {
                  type: isSmallScreen ? "day" : "week",
                  allDay: true,
                  startDay: 1,
                  endDay: 5,
                  startTime: workDayView ? "07:00" : "00:00",
                  endTime: workDayView ? "17:00" : "00:00",
                  timeCellStep: 30,
                  timeLabelStep: 60,
                  currentTimeIndicator: false
                }
              }}
              invalid={invalidRanges}
              dataTimezone="utc"
              displayTimezone={"utc"}
              renderScheduleEventContent={customScheduleEventContentRenderer}
            />
          </Stack>

          {isLargeScreen && (
            <ActionBar
              errors={errorCodes}
              onReset={handleReset}
              showHelp={() => {
                actions.setShowTour(true);
              }}
              onBack={
                prevSavedEventsState?.length > 0
                  ? handleBackToAvailability
                  : undefined
              }
              onSubmit={handleDialogSubmitOpen}
              showBackButton={futureSubmittedAvailabilities?.length > 0}
              isSubmitDisabled={saveButtonEnabled}
              isResetDisabled={!hasAnyEventsChanged}
              isHandlingBack={processingBackActionState}
              middleContent={
                <RequiredSlotsValidation
                  setIsAvailabilityParametersSatisfied={
                    setIsAvailabilityParametersSatisfied
                  }
                  totalSlots={
                    (meetingLink?.scheduledData?.docDataRooster as any)
                      ?.totalInterviewSlots ?? 0
                  }
                  availableDaysRequired={
                    (meetingLink?.scheduledData?.docDataRooster as any)
                      ?.availableDaysRequired ?? 0
                  }
                  minSlotsAcrossDays={
                    (meetingLink?.scheduledData?.docDataRooster as any)
                      ?.minSlotsAcrossDays ?? 0
                  }
                  duration={meetingLink?.availability?.duration ?? 30}
                  currentSlots={[
                    ...filteredPrevSavedEventsState,
                    ...filteredNewEventsState
                  ]}
                  timezone={sessionData?.selectedTimezone ?? moment.tz.guess()}
                  validDates={validDates}
                  inline
                />
              }
            />
          )}
        </Stack>
        <AltChatUI
          view="availability"
          showChatIcon={isSmallScreen || isMediumScreen}
        />
      </Stack>
    </Stack>
  );
};
